import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function Scenarios() {
  const [selectedScenario, setSelectedScenario] = useState<number | null>(null);

  const scenarios = [
    {
      id: 1,
      title: "Christmas Village Magic",
      subtitle: "Pueblo Encantado",
      mainImage: "https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/0ceeb4d072bfa1afdeed63bcda02d024.jpeg",
      icon: 'ri-home-heart-fill',
      description: "Sumérgete en la magia de un pueblo navideño encantado, donde las luces doradas crean una atmósfera cinematográfica única. Este escenario captura la esencia de la Navidad con un toque de fantasía y elegancia.",
      features: [
        { 
          text: 'Luces Encantadas', 
          color: 'gold',
          icon: 'ri-lightbulb-flash-fill',
          description: 'Iluminación profesional que crea un ambiente mágico y cinematográfico'
        },
        { 
          text: 'Ambiente Mágico', 
          color: 'pine',
          icon: 'ri-magic-fill',
          description: 'Decoración detallada que transporta a un mundo de fantasía navideña'
        },
        { 
          text: 'Colores Vibrantes', 
          color: 'cherry',
          icon: 'ri-palette-fill',
          description: 'Combinación perfecta de tonos cálidos y festivos para fotos impactantes'
        }
      ],
      details: {
        elements: [
          'Casitas iluminadas con detalles artesanales',
          'Mercado navideño con puestos decorados',
          'Árboles de Navidad con luces brillantes',
          'Nieve artificial y efectos de iluminación',
          'Decoraciones vintage y modernas combinadas'
        ],
        idealFor: [
          'Familias que buscan un ambiente festivo y alegre',
          'Niños que aman la magia de la Navidad',
          'Grupos grandes que quieren fotos dinámicas',
          'Quienes buscan un estilo colorido y vibrante'
        ],
        gallery: [
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/0ceeb4d072bfa1afdeed63bcda02d024.jpeg',
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/d56c53051caf385b37bcd583e83a7c36.jpeg',
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/5ca7f2b168b78e19243e3a59083aadc9.jpeg',
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/0fc27b7f316c1bc9792b655930699ddf.jpeg'
        ]
      }
    },
    {
      id: 2,
      title: '🚪 The Red Door Christmas',
      subtitle: 'Tradición',
      mainImage: 'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/d6fbf97d6cff4aaf268026231efd5afe.jpeg',
      icon: 'ri-door-open-fill',
      description: 'Una entrada acogedora a la tradición navideña más pura. Nuestro icónico escenario de la puerta roja evoca la calidez del hogar y la bienvenida familiar. Cada fotografía captura la esencia de la hospitalidad navideña, creando imágenes llenas de nostalgia y amor familiar.',
      features: [
        { 
          text: 'Calidez Hogareña', 
          color: 'cherry',
          icon: 'ri-home-heart-fill',
          description: 'Ambiente acogedor que transmite la calidez del hogar en Navidad'
        },
        { 
          text: 'Tradición Familiar', 
          color: 'gold',
          icon: 'ri-group-fill',
          description: 'Escenario perfecto para capturar momentos familiares atemporales'
        },
        { 
          text: 'Nostalgia Navideña', 
          color: 'pine',
          icon: 'ri-heart-fill',
          description: 'Decoración clásica que evoca recuerdos y emociones navideñas'
        }
      ],
      details: {
        elements: [
          'Puerta roja icónica con corona navideña',
          'Decoración clásica y elegante',
          'Iluminación cálida y acogedora',
          'Elementos vintage y tradicionales',
          'Ambiente íntimo y familiar'
        ],
        idealFor: [
          'Familias que valoran la tradición',
          'Retratos elegantes y atemporales',
          'Quienes buscan un estilo clásico',
          'Fotos con ambiente acogedor y cálido'
        ],
        gallery: [
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/8c2760ada0a6d27c2e992dd782f5b7b0.jpeg',
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/bc72e6fffd2d1fe83c39f4cd7beaccff.jpeg',
          'https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/91b2ed7c4c150df6aef70a0b0618ec73.jpeg'
        ]
      }
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Escenarios Navideños - Starlight Christmas",
    "description": "Explora nuestros dos escenarios mágicos: Christmas Village Magic y The Red Door Christmas",
    "url": `${import.meta.env.VITE_SITE_URL}/scenarios`,
    "image": scenarios.map(s => s.mainImage)
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <SEOHead
        title="Escenarios Navideños | Starlight Christmas"
        description="Explora nuestros dos escenarios mágicos: Christmas Village Magic con luces encantadas y The Red Door Christmas con tradición hogareña. Elige el perfecto para tu sesión."
        keywords="escenarios navideños, christmas village, red door christmas, fotografía navideña, sets fotográficos, decoración navideña"
        url={`${import.meta.env.VITE_SITE_URL}/scenarios`}
        structuredData={structuredData}
      />

      {/* Header con navegación */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-black/95 backdrop-blur-md border-b border-gold-500/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 cursor-pointer group">
              <i className="ri-arrow-left-line text-gold-500 text-xl group-hover:translate-x-[-4px] transition-transform"></i>
              <span className="text-gold-500 font-semibold font-sans">Volver al Inicio</span>
            </Link>
            <img 
              src="https://static.readdy.ai/image/d7e77f925f0119a01fe5d8699c3ff655/bcce1cda134b8bda0e0ca09aeb53adfd.png" 
              alt="Starlight Christmas" 
              className="h-12 sm:h-14"
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 lg:pt-48 lg:pb-24 px-4 sm:px-6 lg:px-8">
        {/* Fondo estrellado */}
        <div className="absolute inset-0">
          {[...Array(40)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-pulse opacity-20"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            >
              <div className="w-0.5 h-0.5 rounded-full bg-gold-500"></div>
            </div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 sm:mb-8 leading-tight font-serif text-gold-500">
            Explora Nuestros Escenarios Mágicos
          </h1>
          <div className="w-24 sm:w-32 lg:w-40 h-px mx-auto mb-8 sm:mb-10 bg-gradient-to-r from-transparent via-gold-500 to-transparent"></div>
          <p className="text-lg sm:text-xl lg:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed font-sans">
            Dos mundos únicos diseñados para capturar la magia de la Navidad. Cada escenario cuenta su propia historia y crea recuerdos inolvidables.
          </p>
        </div>
      </section>

      {/* Escenarios Detallados */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-24 sm:space-y-32 lg:space-y-40">
          {scenarios.map((scenario, index) => (
            <div key={scenario.id} className="relative">
              {/* Imagen principal y título */}
              <div className={`grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="relative overflow-hidden rounded-2xl lg:rounded-3xl border border-gold-500/20 group">
                    <img 
                      src={scenario.mainImage}
                      alt={scenario.title}
                      className="w-full h-[400px] sm:h-[500px] lg:h-[600px] object-cover object-top group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-6 left-6 right-6">
                      <div className="flex items-center space-x-3 mb-3 text-gold-500">
                        <i className={`${scenario.icon} text-2xl lg:text-3xl`}></i>
                        <span className="text-sm lg:text-base font-medium tracking-wide font-sans">{scenario.subtitle.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={`space-y-6 sm:space-y-8 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight font-serif text-gold-500">
                    {scenario.title}
                  </h2>
                  <p className="text-base sm:text-lg lg:text-xl leading-relaxed text-gray-200 font-sans">
                    {scenario.description}
                  </p>
                  <button
                    onClick={() => setSelectedScenario(selectedScenario === scenario.id ? null : scenario.id)}
                    className="bg-gold-500 hover:bg-gold-600 text-black font-semibold py-3 sm:py-4 px-6 sm:px-8 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer font-sans"
                  >
                    {selectedScenario === scenario.id ? 'Ver Menos' : 'Ver Detalles Completos'}
                  </button>
                </div>
              </div>

              {/* Detalles expandibles */}
              {selectedScenario === scenario.id && (
                <div className="mt-12 sm:mt-16 lg:mt-20 space-y-12 sm:space-y-16 animate-fadeIn">
                  {/* Características */}
                  <div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-10 text-center font-serif text-gold-500">
                      Características del Escenario
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                      {scenario.features.map((feature, idx) => (
                        <div key={idx} className="bg-gray-900/50 border border-gold-500/20 rounded-xl p-6 hover:border-gold-500/40 transition-all duration-300">
                          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gold-500/20 mb-4">
                            <i className={`${feature.icon} text-2xl text-gold-500`}></i>
                          </div>
                          <h4 className="text-lg sm:text-xl font-semibold mb-2 text-gold-400 font-serif">{feature.text}</h4>
                          <p className="text-sm sm:text-base text-gray-400 font-sans">{feature.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Galería */}
                  <div>
                    <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-8 sm:mb-10 text-center font-serif text-gold-500">
                      Galería del Escenario
                    </h3>
                    <div className="columns-1 sm:columns-2 gap-6 sm:gap-8 space-y-6 sm:space-y-8">
                      {scenario.details.gallery.map((image, idx) => (
                        <div key={idx} className="relative overflow-hidden rounded-xl border border-gold-500/20 group bg-black break-inside-avoid">
                          <img 
                            src={image}
                            alt={`${scenario.title} - Vista ${idx + 1}`}
                            className="w-full h-auto object-contain group-hover:scale-105 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Ideal para */}
                  <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 border border-gold-500/30 rounded-2xl p-8 sm:p-10 lg:p-12">
                    <h3 className="text-2xl sm:text-3xl font-bold mb-6 text-center font-serif text-gold-500">
                      Ideal Para
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4 sm:gap-6">
                      {scenario.details.idealFor.map((item, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <div className="w-2 h-2 rounded-full bg-gold-500 flex-shrink-0"></div>
                          <span className="text-base sm:text-lg text-gray-200 font-sans">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black to-gray-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 sm:mb-8 font-serif text-gold-500">
            ¿Listo para Crear Recuerdos Mágicos?
          </h2>
          <p className="text-lg sm:text-xl text-gray-200 mb-8 sm:mb-10 font-sans">
            Elige tu escenario favorito y reserva tu sesión navideña hoy mismo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link
              to="/"
              onClick={() => {
                setTimeout(() => {
                  const servicesSection = document.getElementById('services-section');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ 
                      behavior: 'smooth',
                      block: 'start'
                    });
                  }
                }, 300);
              }}
              className="bg-gold-500 hover:bg-gold-600 text-black font-semibold py-4 px-8 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer font-sans"
            >
              Ver Paquetes y Reservar
            </Link>
            <button 
              onClick={() => {
                window.scrollTo({ 
                  top: 0, 
                  behavior: 'smooth' 
                });
              }}
              className="bg-transparent border-2 border-gold-500 hover:bg-gold-500/10 text-gold-500 font-semibold py-4 px-8 rounded-xl transition-all duration-300 whitespace-nowrap cursor-pointer font-sans"
            >
              Ver Escenarios Nuevamente
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
